import { keys } from 'ramda';
import React from 'react';
import styled, { css } from 'styled-components';
import TEST_ID from './index.testid';

import useViewingModeProps from '~/hooks/useViewingModeProps';
import { restrictedFields } from '~/components/organism/Table/constants';

export type Columns = Record<
  string,
  {
    header: React.ReactNode;
    format?: (
      value: React.ReactNode,
      row?: Row,
      columnName?: string,
    ) => React.ReactNode;
  }
>;

export type Row = Record<string, React.ReactNode | any>;

export type Props = {
  columns: Columns;
  rows: Array<Row>;
  className?: string;
  blurredColumns?: Array<string>;
};

const format = (columns: Columns, columnName: string, row: Row) => {
  const formatter = columns[columnName].format;
  return formatter
    ? formatter(row[columnName], row, columnName)
    : row[columnName];
};

/*
  If you need column sorting or pagination, this is not the table you're looking for (http://gph.is/1W9lKoB)
*/
const SimpleTable: React.FCC<Props> = ({
  dataTestId,
  columns,
  rows,
  blurredColumns = [],
  ...rest
}) => {
  const viewingModeProps = useViewingModeProps();

  return (
    <ContainerTable data-testid={dataTestId} {...rest}>
      <thead>
        <tr>
          {keys(columns).map((column, index) => (
            <HeaderCell key={`header-${index}`} data-testid={TEST_ID.HEADER}>
              {columns[column].header}
            </HeaderCell>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index} data-testid={TEST_ID.ROW}>
            {keys(columns).map(columnName => (
              <td
                key={columnName}
                data-blurred={
                  blurredColumns.includes(columnName) &&
                  row.permission?.__typename === 'Permission_AccessDenied_Plan'
                }
                data-redacted={
                  viewingModeProps['data-redacted'] &&
                  restrictedFields.includes(columnName)
                }
              >
                {format(columns, columnName, row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </ContainerTable>
  );
};

const ContainerTable = styled.table<{}>(
  ({ theme }) => css`
    border-spacing: ${theme.space('xs')};
  `,
);

const HeaderCell = styled.th<{}>(
  ({ theme }) => css`
    text-align: left;
    font-weight: ${theme.fontWeight('semiBold')};
  `,
);

export default SimpleTable;
